import React, { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import VideoLoader from "@fuse/sharedComponents/videoLoader"
import moment from "moment"
import { useRecoilState } from "recoil"

import { userState } from "@fuse/recoil/signup"

import {
  followOrganizationAPI,
  getOrganizationAPI,
  getOrganizationEventsAPI,
  unfollowOrganizationAPI,
} from "@api/firebaseWrappedApis"

import FastSignUpComponent from "@fuse/pages/eventPage/FastSignUpComponent"

interface Organization {
  id: string
  name: string
  description: string
  profileImage: string
  backgroundImage: string
  bio_html: string
  page_html: string
  followerCount: number
  isFollowing?: boolean
  totalEvents?: number
  totalGuestsServed?: number
  eventHostRating?: number
}

interface Event {
  id: string
  title: string
  description: string
  location: string
  timing: {
    eventDate: string
    eventStartTime: string
    eventEndTime: string
  }
  coverImage: string
  hostName: string
  price?: number
}

const OrganizationProfile: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [events, setEvents] = useState<Event[]>([])
  const [activeTab, setActiveTab] = useState("upcoming")
  const [loading, setLoading] = useState(true)
  const [showLoader, setShowLoader] = useState(true)
  const [followLoading, setFollowLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useRecoilState(userState)
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  const [pendingFollow, setPendingFollow] = useState(false)
  const { showFastSignUpPopUp, setShowFastSignUpPopUp } = useSignUpModalState()

  // When data is loaded, signal that the content is ready but keep the loader visible
  // to allow it to complete its animation cycle naturally
  useEffect(() => {
    if (!loading && organization) {
      // We don't immediately set showLoader to false
      // Instead, we'll let the VideoLoader component handle its own visibility
      // when its animation completes
    }
  }, [loading, organization])

  useEffect(() => {
    const fetchOrganization = async () => {
      setLoading(true)

      try {
        const response: any = await getOrganizationAPI({
          organizationId: orgId || "",
        })

        if (response.success) {
          setOrganization(response.data)
        } else {
          setError(response.message || "Failed to load organization")
        }
      } catch (error) {
        console.error("Error fetching organization:", error)
        setError("An error occurred while fetching the organization")
      } finally {
        setLoading(false)
      }
    }

    const fetchEvents = async () => {
      try {
        const response: any = await getOrganizationEventsAPI({
          organizationId: orgId || "",
          isPast: activeTab === "past",
          limit: 10,
        })

        if (response.success) {
          setEvents(response.data || [])
        } else {
          console.error("Error in events response:", response.message)
        }
      } catch (error) {
        console.error("Error fetching events:", error)
      }
    }

    if (orgId) {
      fetchOrganization()
      fetchEvents()
    }
  }, [orgId, activeTab])

  // Effect to handle automatic follow after login
  useEffect(() => {
    const handlePendingFollow = async () => {
      if (pendingFollow && user.isLoggedIn && organization) {
        setPendingFollow(false)
        setFollowLoading(true)

        try {
          const response: any = await followOrganizationAPI({
            organizationId: orgId || "",
          })

          if (response.success) {
            setOrganization({
              ...organization,
              isFollowing: true,
              followerCount: organization.followerCount + 1,
            })
          }
        } catch (error) {
          console.error("Error following organization after login:", error)
        } finally {
          setFollowLoading(false)
        }
      }
    }

    handlePendingFollow()
  }, [user.isLoggedIn, pendingFollow, organization, orgId])

  const handleFollow = async () => {
    if (!organization) return

    // Set loading immediately when button is clicked
    setFollowLoading(true)

    // Check if user is logged in
    if (!user.isLoggedIn) {
      // Show signup modal if not logged in
      setPendingFollow(true)
      setShowSignUpModal(true)
      setFollowLoading(false)
      return
    }

    // Optimistically update UI
    const wasFollowing = organization.isFollowing
    const newFollowerCount = wasFollowing
      ? organization.followerCount - 1
      : organization.followerCount + 1

    setOrganization({
      ...organization,
      isFollowing: !wasFollowing,
      followerCount: newFollowerCount,
    })

    try {
      const response: any = wasFollowing
        ? await unfollowOrganizationAPI({ organizationId: orgId || "" })
        : await followOrganizationAPI({ organizationId: orgId || "" })

      if (!response.success) {
        // Revert if API call failed
        setOrganization({
          ...organization,
          isFollowing: wasFollowing,
          followerCount: organization.followerCount,
        })
      }
    } catch (error) {
      console.error("Error toggling follow status:", error)
      // Revert on error
      setOrganization({
        ...organization,
        isFollowing: wasFollowing,
        followerCount: organization.followerCount,
      })
    } finally {
      setFollowLoading(false)
    }
  }

  return (
    <>
      {/* Only render the VideoLoader if showLoader is true */}
      {showLoader && (
        <VideoLoader loading={loading} setShowLoader={setShowLoader} />
      )}

      <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-800 to-gray-900">
        <Navbar />

        {/* Login/Signup Modal */}
        <FastSignUpComponent
          isOpen={showSignUpModal}
          setOpenState={(isOpen) => {
            setShowSignUpModal(isOpen)
            // Clear pending follow if modal is closed without completing sign-up
            if (!isOpen && !user.isLoggedIn) {
              setPendingFollow(false)
            }
          }}
          title={organization?.name || "this organization"}
        />

        {loading ? (
          // Just an empty div while loading since VideoLoader is handling loading state
          <div className="flex-1"></div>
        ) : error || !organization ? (
          // Error display
          <div className="flex-1 flex justify-center items-center">
            <div className="bg-gray-700 p-8 rounded-lg shadow-md max-w-md w-full text-center">
              <div className="text-red-400 text-5xl mb-4">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                Organization Not Found
              </h1>
              <p className="text-gray-300 mb-6">
                {error ||
                  "We couldn't find the organization you're looking for."}
              </p>
              <button
                onClick={() => window.history.back()}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition duration-300"
              >
                Go Back
              </button>
            </div>
          </div>
        ) : (
          // Content when organization is loaded
          <>
            {/* Background header with profile image */}
            <div className="relative">
              {/* Background Image */}
              <div
                className="h-48 w-full bg-cover bg-center"
                style={{
                  backgroundImage: `url(${organization.backgroundImage || "https://via.placeholder.com/1200x400"})`,
                }}
              />

              {/* Profile Image (positioned to overlap) */}
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-16">
                <img
                  src={
                    organization.profileImage ||
                    "https://via.placeholder.com/200"
                  }
                  alt={organization.name}
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                />
              </div>
            </div>

            <div className="container mx-auto px-4 pt-20 pb-20">
              {/* Profile Header */}
              <div className="flex flex-col items-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {organization.name}
                </h1>

                <p className="text-gray-300 text-center max-w-lg mb-6">
                  {organization.description}
                </p>

                {/* Bio HTML */}
                {organization.bio_html && (
                  <div className="mb-6 prose prose-invert max-w-none mx-auto text-center">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: organization.bio_html,
                      }}
                    />
                  </div>
                )}

                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`px-8 py-2 rounded-full font-medium transition duration-300 relative ${
                    organization.isFollowing
                      ? "bg-gray-600 text-white hover:bg-gray-700"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  } ${followLoading ? "opacity-80" : ""}`}
                >
                  {followLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {organization.isFollowing ? "Following" : "Follow"}
                    </span>
                  ) : organization.isFollowing ? (
                    "Following"
                  ) : (
                    "Follow"
                  )}
                </button>
              </div>

              {/* Events Section */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-4 text-left">
                  {organization.name}'s events
                </h2>

                {/* Tabs */}
                <div className="flex mb-6">
                  <button
                    className={`py-2 px-6 font-medium ${
                      activeTab === "upcoming"
                        ? "text-white border-b-2 border-blue-500"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                    onClick={() => setActiveTab("upcoming")}
                  >
                    Upcoming Events
                  </button>
                  <button
                    className={`py-2 px-6 font-medium ${
                      activeTab === "past"
                        ? "text-white border-b-2 border-blue-500"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                    onClick={() => setActiveTab("past")}
                  >
                    Past Events
                  </button>
                </div>

                {/* Events List */}
                {events.length === 0 ? (
                  <div className="text-center py-12 bg-gray-800 bg-opacity-50 rounded-lg">
                    <h3 className="text-xl font-medium text-white mb-2">
                      No {activeTab} events
                    </h3>
                    <p className="text-gray-400">
                      This organization has no {activeTab} events scheduled.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {events.map((event) => (
                      <a
                        key={event.id}
                        href={`/events/${event.id}`}
                        className="block bg-gray-800 bg-opacity-50 rounded-lg overflow-hidden hover:bg-gray-700 transition"
                      >
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-1/4">
                            <img
                              src={
                                event.coverImage ||
                                "https://via.placeholder.com/400x200"
                              }
                              alt={event.title}
                              className="w-full h-48 md:h-full object-cover"
                            />
                          </div>
                          <div className="p-4 md:p-6 flex-1">
                            <h3 className="text-xl font-semibold text-white mb-2">
                              {event.title}
                            </h3>
                            <div className="flex items-center text-gray-300 mb-2">
                              <span className="mr-2">📅</span>
                              <span>
                                {moment(event.timing.eventDate).format(
                                  "ddd M/D",
                                )}{" "}
                                • {event.timing.eventStartTime}
                              </span>
                            </div>
                            <div className="flex items-center text-gray-300 mb-4">
                              <span className="mr-2">📍</span>
                              <span>{event.location}</span>
                            </div>
                            {event.price !== undefined && (
                              <div className="inline-block bg-gray-700 text-white px-3 py-1 rounded">
                                ${event.price}
                              </div>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Page HTML */}
              {organization.page_html && (
                <div className="mt-8 prose prose-invert max-w-none mx-auto px-4">
                  <div
                    dangerouslySetInnerHTML={{ __html: organization.page_html }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default OrganizationProfile
